import { classificationTypes } from '@/components/Studies/Treatments/Treatment.utils';
import { errorToast } from '@/helpers';
import { _isNil } from '@/littledash';
import type { Study } from '@/model/Study.model';
import type { ClassificationType, Treatment } from '@/model/Treatment.model';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import useMountedState from '@/support/Hooks/fetch/useMountedState';
import { useModalAction } from '@/utils/modal';

interface OverviewTreatmentInfoModal {
  loadingTreatment: boolean;
  openTreatmentInfo: (apiId: string, classification: ClassificationType) => Promise<void>;
}

/**
 * Overview rows only carry display strings. A label click loads the full treatment
 * and opens the same info modal as the Treatments column on the groups table.
 */
export const useOverviewTreatmentInfoModal = (studyApiId: Study['api_id']): OverviewTreatmentInfoModal => {
  const isMounted = useMountedState();
  const { openModal, closeModal } = useModalAction();
  const { loading, invoke } = useApiHook({
    endpoint: 'GET /api/v1/studies/{studyApiId}/treatments',
    options: {
      invokeOnInit: false,
      onError: { toast: false, capture: true, throw: true, slug: 'overview-treatment-load' },
    },
  });

  const openTreatmentInfo = async (apiId: string, classification: ClassificationType): Promise<void> => {
    const treatment = await invoke({
      path: { studyApiId },
      query: {
        include: ['study_groups', 'metadata'],
        classification: [classification],
        perPage: -1,
      },
    })
      .then((response) =>
        response.type === 'success'
          ? (response.body.data as unknown as Array<Treatment>).find(
              ({ api_id: treatmentApiId }) => treatmentApiId === apiId
            )
          : undefined
      )
      .catch(() => undefined);

    if (!isMounted()) {
      return;
    }

    if (_isNil(treatment)) {
      errorToast(`Could not load ${classificationTypes[classification].key}`);
      return;
    }

    openModal('TREATMENT_META', { treatment, closeModal });
  };

  return { loadingTreatment: loading, openTreatmentInfo };
};
